import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MessageCircle, XCircle } from "lucide-react";
import { API } from "@/lib/api";

const WHATSAPP_CONFIRM = `https://wa.me/553196335635?text=${encodeURIComponent("Olá Luiz! Acabei de fazer o pagamento pelo site e quero começar meu projeto.")}`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [source, setSource] = useState("conta");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.source) setSource(data.source);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setState("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-4 text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-2xl border border-[#22222A] bg-[#101014] p-10 text-center shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
      >
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#E50914]" />
            <h1 className="mt-6 font-display text-2xl font-bold">Confirmando pagamento...</h1>
            <p className="mt-2 text-sm text-zinc-500">Só um instante, estamos validando com a Stripe.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
              <CheckCircle2 className="h-9 w-9 text-emerald-400" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold">Pagamento confirmado!</h1>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Obrigado! Seu pagamento foi aprovado. O Luiz já foi notificado e entra em contato no seu WhatsApp em até 24h.
            </p>
            <a
              data-testid="payment-whatsapp-cta"
              href={WHATSAPP_CONFIRM}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-red-glow mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E50914] px-8 py-3.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
            >
              <MessageCircle className="h-4 w-4" />
              Avisar o Luiz no WhatsApp
            </a>
            <a
              data-testid="payment-back-account"
              href={source === "planos" ? "/" : "/conta"}
              className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-[#2A2A33] px-8 py-3.5 font-mono-code text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#E50914]/60 hover:text-white"
            >
              {source === "planos" ? "Voltar ao site" : "Voltar para minha conta"}
            </a>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
              <XCircle className="h-9 w-9 text-amber-400" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold">
              {state === "timeout" ? "Pagamento em processamento" : "Sessão inválida"}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              {state === "timeout"
                ? "Ainda estamos confirmando seu pagamento. Se o valor foi debitado, ele aparece na sua conta em instantes."
                : "Não encontramos esta sessão de pagamento."}
            </p>
            <a
              data-testid="payment-back-account-pending"
              href={source === "planos" ? "/" : "/conta"}
              className="btn-red-glow mt-8 inline-flex items-center justify-center rounded-full bg-[#E50914] px-8 py-3.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
            >
              {source === "planos" ? "Voltar ao site" : "Voltar para minha conta"}
            </a>
          </div>
        )}
      </motion.div>
    </div>
  );
}
